import { z } from 'zod';

// Sanitization helper
export const sanitizeInput = (input: string): string => {
  return input.trim().replace(/[<>]/g, '');
};

// Contact Form Validation Schema
export const contactFormSchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters')
    .transform(sanitizeInput),
  email: z.string()
    .min(1, 'Email is required')
    .email('Invalid email address')
    .max(255, 'Email must be less than 255 characters')
    .transform(sanitizeInput),
  phone: z.string()
    .max(20, 'Phone number must be less than 20 characters')
    .optional()
    .transform(val => val ? sanitizeInput(val) : val),
  subject: z.string()
    .min(1, 'Subject is required')
    .max(200, 'Subject must be less than 200 characters')
    .transform(sanitizeInput),
  inquiryType: z.string()
    .min(1, 'Please select an inquiry type'),
  message: z.string()
    .min(10, 'Message must be at least 10 characters')
    .max(5000, 'Message must be less than 5000 characters')
    .transform(sanitizeInput),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

// Lead Form Validation Schema
export const leadFormSchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters')
    .transform(sanitizeInput),
  email: z.string()
    .min(1, 'Email is required')
    .email('Invalid email address')
    .max(255, 'Email must be less than 255 characters')
    .transform(sanitizeInput),
  phone: z.string()
    .max(20, 'Phone number must be less than 20 characters')
    .optional()
    .transform(val => val ? sanitizeInput(val) : val),
  message: z.string()
    .max(2000, 'Message must be less than 2000 characters')
    .optional()
    .transform(val => val ? sanitizeInput(val) : val),
});

export type LeadFormData = z.infer<typeof leadFormSchema>;

// NDA Form Validation Schema
export const ndaFormSchema = z.object({
  buyerName: z.string()
    .min(1, 'Full name is required')
    .max(100, 'Name must be less than 100 characters')
    .transform(sanitizeInput),
  buyerEmail: z.string()
    .min(1, 'Email is required')
    .email('Invalid email address')
    .max(255, 'Email must be less than 255 characters')
    .transform(sanitizeInput),
  hasAgreed: z.boolean()
    .refine(val => val === true, 'You must agree to the NDA terms'),
});

export type NDAFormData = z.infer<typeof ndaFormSchema>;

// Rate limiting helper (client-side basic protection)
const rateLimitStore = new Map<string, { count: number; resetAt: number }>();

export const checkRateLimit = (key: string, maxAttempts: number = 3, windowMs: number = 3600000): boolean => {
  const now = Date.now();
  const record = rateLimitStore.get(key);

  if (!record || now > record.resetAt) {
    rateLimitStore.set(key, { count: 1, resetAt: now + windowMs });
    return true;
  }

  if (record.count >= maxAttempts) {
    return false;
  }

  record.count++;
  return true;
};

export const getRateLimitResetTime = (key: string): number | null => {
  const record = rateLimitStore.get(key);
  if (!record) return null;
  return Math.max(0, record.resetAt - Date.now());
};
